import React, { useState, useCallback } from 'react';
import { Edit3, Save, X, Loader2 } from 'lucide-react';
import { BiographyContentProps } from './types';
import BiographyField from './BiographyField';
import BiographyCard from './BiographyCard';
import Button from '../Common/Button';
import './BiographySystem.css';

interface EditableContentProps extends BiographyContentProps {
  onSave: () => Promise<void>;
  onCancel?: () => void;
  autoSave?: boolean;
  autoSaveDelay?: number;
  useCard?: boolean;
}

/**
 * EditableContent - Handles edit/save behavior for biography fields
 * Provides edit mode toggle, validation, and save/cancel functionality
 */
const EditableContent: React.FC<EditableContentProps> = ({
  fields,
  onFieldChange,
  onSave,
  onCancel,
  maxFieldsPerRow = 1,
  showLabels = true,
  autoSave = false,
  autoSaveDelay = 2000,
  useCard = true
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [originalValues, setOriginalValues] = useState<Record<string, string | string[]>>({});

  // Initialize original values when entering edit mode
  const startEditing = useCallback(() => {
    const values: Record<string, string | string[]> = {};
    fields.forEach(field => {
      values[field.id] = field.value;
    });
    setOriginalValues(values);
    setIsEditing(true);
    setErrors({});
  }, [fields]);

  // Cancel editing and restore original values
  const cancelEditing = useCallback(() => {
    // Restore original values
    Object.entries(originalValues).forEach(([fieldId, value]) => {
      onFieldChange(fieldId, value);
    });

    setIsEditing(false);
    setErrors({});
    if (onCancel) {
      onCancel();
    }
  }, [originalValues, onFieldChange, onCancel]);

  // Validate all fields
  const validateFields = useCallback(() => {
    const newErrors: Record<string, string> = {};

    fields.forEach(field => {
      if (field.validation) {
        const error = field.validation(field.value);
        if (error) {
          newErrors[field.id] = error;
        }
      }

      if (field.required) {
        const value = field.value;
        const isEmpty = !value ||
          (typeof value === 'string' && value.trim() === '') ||
          (Array.isArray(value) && value.length === 0);

        if (isEmpty) {
          newErrors[field.id] = `${field.label} is required`;
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [fields]);

  // Save changes
  const saveChanges = useCallback(async () => {
    if (!validateFields()) {
      return;
    }

    setIsSaving(true);
    try {
      await onSave();
      setIsEditing(false);
      setErrors({});
    } catch (error) {
      console.error('Failed to save changes:', error);
      // Keep in edit mode if save fails
    } finally {
      setIsSaving(false);
    }
  }, [validateFields, onSave]);

  // Handle field changes with validation
  const handleFieldChange = useCallback((fieldId: string, value: string | string[]) => {
    onFieldChange(fieldId, value);

    // Clear error for this field if it exists
    if (errors[fieldId]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldId];
        return newErrors;
      });
    }
  }, [onFieldChange, errors]);

  // Check if any fields have been modified
  const hasChanges = fields.some(field => {
    const original = originalValues[field.id];
    const current = field.value;

    if (Array.isArray(original) && Array.isArray(current)) {
      return JSON.stringify(original) !== JSON.stringify(current);
    }

    return original !== current;
  });

  const gridClassName = maxFieldsPerRow > 1
    ? `biography-fields biography-fields--grid biography-fields--grid-${Math.min(maxFieldsPerRow, 3)}`
    : 'biography-fields';

  const content = (
    <>
      {/* Fields */}
      <div className={gridClassName}>
        {fields.map(field => (
          <BiographyField
            key={field.id}
            field={{
              ...field,
              editable: field.editable && isEditing
            }}
            value={field.value}
            onChange={(value) => handleFieldChange(field.id, value)}
            showLabel={showLabels}
            error={errors[field.id]}
          />
        ))}
      </div>

      {/* Actions */}
      <div className="biography-actions biography-actions--right">
        {!isEditing ? (
          <Button
            variant="SECONDARY"
            size="SMALL"
            onClick={startEditing}
            disabled={!fields.some(f => f.editable)}
          >
            <Edit3 size={14} />
            Edit
          </Button>
        ) : (
          <>
            <Button
              variant="CLEAR"
              size="SMALL"
              onClick={cancelEditing}
              disabled={isSaving}
            >
              <X size={14} />
              Cancel
            </Button>
            <Button
              variant="PRIMARY"
              size="SMALL"
              onClick={saveChanges}
              disabled={isSaving || !hasChanges || Object.keys(errors).length > 0}
            >
              {isSaving ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={14} />
                  Save Changes
                </>
              )}
            </Button>
          </>
        )}
      </div>
    </>
  );

  if (!useCard) {
    return content;
  }

  return (
    <BiographyCard variant="default" padding="md">
      {content}
    </BiographyCard>
  );
};

export default EditableContent;